import type { RefObject } from 'react';
import type {
  IExchangeRates,
  User,
  UserStatus,
  UserWallet,
  Wallet,
} from '@/api/auth';
import type {
  BonanzaPromoMenuDataApi,
  CMSGameCategory,
  CmsABTest,
  DiceRollMenuDataApi,
  FortuneWheelMenuDataApi,
} from '@/api/cms';
import type { AboutUsApi } from '@/api/cms/aboutUs';
import type { CMSImageBase } from '@/api/cms/base';
import type { CMSGameTheme } from '@/api/cms/gameThemes';
import type { LoyaltyPageApi } from '@/api/cms/loyalty';
import type { OurBenefits } from '@/api/cms/ourBenefits';
import type { ScratchGameMenuDataApi } from '@/api/cms/scratchGame';
import type { ThirdPartySummary } from '@/api/cms/thirdPartySummaries';
import type { Tournament } from '@/api/cms/tournaments';
import type { VipApi } from '@/api/cms/vip';
import type {
  Bonus,
  Currency,
  Freespin,
  Jackpot,
  Lootbox,
  LootboxBonus,
  PlayerJackpotWin,
  PlayerTransaction,
} from '@/api/common';
import type { CompPoints } from '@/api/compPoints';
import type { GamesFilter, GameV1, Real, RecentGame } from '@/api/games';
import type { ICMSConfigApi } from '@/modules/common';
import type { IPlayerBalance } from '@/modules/common/hooks/usePlayerAccounts';
import type { TopPlayer } from '@/modules/common/hooks/useTournaments';
import type { Limit } from '@/modules/common/hooks/useUserLimits';
import type { LoyaltyDataI } from '@/modules/loyalty/components/helpers';
import type { ISSEInitEvent } from '@/modules/loyaltyNew/api/loyalty-sse';
import type {
  BonanzaPromoStatus,
  EScratchTicketTheme,
} from '@/modules/promotions';
import type { IHeadlinePromoPageConfig } from '@/modules/promotions/templates/HeadlinePromoTemplate/helpers';
import type { RiskSegment } from '@/modules/riskSegment/constants';
import type { REASON_FOR_UPDATING_USER } from './slices/auth/helpers';

export interface PlayerState {
  lastSuccessfulPaymentsByCurrency: Record<string, number>;
  lastSuccessfulPaymentsByCurrencyPending: boolean;
  isAuthenticatedByCookie: boolean;
}

export enum License {
  CW = 'CW',
  EE = 'EE',
}

export interface TechInfoState {
  isMobileDevice: boolean;
  gameOpenBackUrl: string | null;
  pendingStore: boolean;
  serverError?: string | null;
  hostname: string;
  userIP: string | null;
  cmsABTests: CmsABTest[];
  cmsConfig: ICMSConfigApi | null;

  countryCode: string;
  regionCode: string;
  enableCurrencies: Currency[];
  formatAmountByCurrency: (amount: number, currency: string) => number;

  tabBarHeight: number;
  handleTabBarHeight: (ref: RefObject<HTMLDivElement>) => void;

  setCountryCodeData: (isEstonia: boolean) => void;

  exchangeRates: IExchangeRates | null;
  riskSegment: RiskSegment;
}

interface AuthActions {
  fetchUser: (
    reasonForUpdatingUser: REASON_FOR_UPDATING_USER,
    isActiveSessionBackend?: boolean
  ) => Promise<User | void>;
  refreshUser: () => Promise<User | void>;
  updateUser: (user: Partial<User>, reason?: REASON_FOR_UPDATING_USER) => void;
  insertUser: (
    user: User,
    reasonForUpdatingUser: REASON_FOR_UPDATING_USER
  ) => Promise<User | void>;
  resetUser: () => Promise<User | void>;

  setUserWallets: (wallets: Wallet[]) => Promise<User | void>;
  changeUserWallet: (currency: Wallet['currency']) => Promise<User | void>;
  deleteUserWallet: (
    prevCurrency: Wallet['currency'],
    nextCurrency?: Wallet['currency']
  ) => Promise<User | void>;
  updatePlayerWallet: (
    currency: Wallet['currency'],
    user: User
  ) => Promise<User | void>;
  updatePlayerBalance: (balance: IPlayerBalance) => void;

  setEmailConfirmationPopupSeen: () => void;
  setEmailConfirmationPopupHeight: (height: number) => void;
}

export type UserState = {
  user: User | undefined;
  guestLanguage?: string;
  reasonForUpdatingUser: REASON_FOR_UPDATING_USER | undefined;
  isAuthenticated: boolean;
  pendingUser: boolean;
  pendingUserPure: boolean;
  wallets: Wallet[];
  pendingWallets: boolean;

  afterLoginRedirectUrl: string | null;
  afterAdditionalDataRedirectUrl: string | null;
  afterRegistrationRedirectUrl: string | null;
  afterDepositRedirectUrl: string | null;
};

interface AuthData extends UserState {
  extendedUserWallets: UserWallet[];
  currentWallet: UserWallet | null;
  isEmailConfirmationPopup: boolean;
  emailConfirmationPopupHeight: number;
  limits: Limit[];
}

export type AuthState = AuthData & AuthActions;

export interface GameCovers {
  [key: string]: { cover: CMSImageBase; gradientColor: string };
}

interface RegBanner {
  title: string;
  offer1: string[];
  offer2?: string;
  background?: string;
  character?: string;
  titleColor?: string;
  offerColor?: string;
}

export interface AddonsState {
  ourBenefits: OurBenefits | null;
  aboutUs: AboutUsApi | null;
  vip: VipApi | null;
  loyaltyData: LoyaltyPageApi | null;
  loyaltyPending: boolean;
  regBannerActionBtnRect: DOMRect | null;
  thirdPartySummaries: ThirdPartySummary[] | undefined;
  pendingThirdPartySummaries: boolean;
  availableCrossSell: boolean;

  currentCancelBonusData: { isBonusType: boolean; id: number } | undefined;

  getLoyaltyData: () => LoyaltyDataI | undefined;

  isVerifyMessage: boolean;
  isKnowYourLimit: boolean | undefined;
  afterRegStep: boolean | undefined;

  isPrefilledNdbCoupon: boolean;
  prefilledNdbCoupon: string | undefined;

  regBanner: RegBanner | null;
  registrationTheme: string;
  initialRegFormCurrency?: string | null;
  isNavigateOnCloseRegistration: boolean;
}

export interface BonanzaPromoState {
  isBonanzaInitialized: boolean;
  userBonanzaGroup: BonanzaPromoStatus;
  userBonanzaGroupKey: string;
  bonanzaReward: string;
  bonanzaProgressPercent: number;
  bonanzaPromoMenuData: BonanzaPromoMenuDataApi | null;
  pendingBonanzaPromoMenuData: boolean;
  fetchBonanzaPromoMenuData: (locale: string) => void;
}

export interface IGamesInfo {
  games: GameV1[];
  pagination: GamesFilter.Pagination;
  isFailed?: boolean;
}
export type GamesByParameterData = {
  [parameter: string]: IGamesInfo;
};

type GamesFilterParam = Pick<GamesFilter.Body, 'pageSize' | 'page' | 'sort'> & {
  subName?: string; // to separate different lists with the same category or provider
  staticAmount?: boolean;
};

interface GamesData {
  gameThemes: CMSGameTheme[];

  recentGames: GameV1[];
  pendingRecentGames: boolean;

  favoriteGames: GameV1[];
  favoriteGamesIds: number[];
  pendingFavoriteGames: boolean;

  gamesByCategories: GamesByParameterData;
  pendingGamesByCategories: { [category: string]: boolean };

  gamesByProviders: GamesByParameterData;
  pendingGamesByProviders: { [provider: string]: boolean };
  isLiveSpinsAvailable: boolean;

  gameCategories: CMSGameCategory[];
}

interface GamesActions {
  fetchGames: (
    body?: Omit<GamesFilter.Body, 'currencies'> &
      Partial<Pick<GamesFilter.Body, 'currencies'>>,
    onError?: (error?: any) => void
  ) => Promise<GamesFilter.Response | undefined>;
  fetchGamesByIds: (
    body: GamesFilter.BodySelect,
    isMobileDevice: boolean
  ) => Promise<GamesFilter.ResponseSelect | undefined>;

  fetchRecentGames: (isMobileDevice: boolean) => Promise<void>;

  fetchFavoriteGames: () => Promise<void>;
  addFavoriteGame: (currencies: Real) => Promise<void>;
  removeFavoriteGame: (currency: Real) => Promise<void>;

  getGamesByCategory: (
    category: string,
    filter: GamesFilterParam
  ) => Promise<IGamesInfo | undefined>;
  addGamesByCategory: (gamesInfo: GamesByParameterData) => void;
  togglePendingGamesByCategories: (category: string) => void;

  getGamesByProvider: (
    provider: string,
    filter: GamesFilterParam
  ) => Promise<IGamesInfo | undefined>;
  addGamesByProvider: (gamesInfo: GamesByParameterData) => void;
  togglePendingGamesByProviders: (provider: string) => void;
}

export type GamesState = GamesData & GamesActions;

interface RealTimeServiceActions {
  setActiveJackpot: () => Promise<void>;
  addPlayerJackpotWin: (jackpotWin: PlayerJackpotWin) => void;

  fetchLootboxes: () => Promise<void>;
  updateLootboxes: (lootbox: Lootbox) => void;

  fetchBonuses: () => Promise<void>;
  updateBonuses: (bonus: Bonus) => void;

  fetchFreespins: () => Promise<void>;
  updateFreespins: (freespin: Freespin) => void;

  updatePlayerPayments: (payment: PlayerTransaction) => void;

  fetchRecentGamesIds: () => Promise<void>;
  updateRecentGames: (game: RecentGame) => void;

  fetchCompPoints: () => Promise<void>;
  updateCompPoints: (compPoints: CompPoints) => void;

  updateUserStatuses: (statuses: UserStatus[]) => void;
}
interface RealTimeServiceData {
  activeJackpot: Jackpot | undefined;
  playerJackpotWins: PlayerJackpotWin[];
  pendingJackpots: boolean;

  wheelLootboxes: Lootbox[];
  scratchLootboxes: Lootbox[];
  diceLootboxes: Lootbox[];
  withoutTagsLootboxes: Lootbox[];
  pendingLootboxes: boolean;

  bonusesPending: boolean;
  bonuses: Bonus[];
  hasUserUsedBonuses: null | boolean;

  freespinsPending: boolean;
  freespins: Freespin[];
  hasUserUsedFreespins: null | boolean;

  playerPayments: PlayerTransaction[];
  recentGamesIds: string[];

  activeLootboxId: number | undefined;
  dismissedLootboxId: number | undefined;

  compPoints: CompPoints | undefined;
  compPointsPending: boolean;
}

export type RealTimeServiceState = RealTimeServiceActions & RealTimeServiceData;

export interface HeadlinePromoState {
  headlinePromoConfigs: IHeadlinePromoPageConfig[];
}

export interface TournamentsState {
  tournaments: Tournament[];
  userTournamentsStatuses: TopPlayer[];
  activeTournaments: Tournament[];
  fetchUserTournamentStatus: () => Promise<void>;
  updateUserTournamentStatus: (newTournamentStatus: TopPlayer) => void;
}

export interface ScratchGameState {
  scratchGameMenuData: ScratchGameMenuDataApi | null;
  pendingScratchGameMenuData: boolean;
  fetchScratchGameMenuData: (locale: string) => Promise<void>;
  scratchGameTheme: EScratchTicketTheme;
  setScratchGameTheme: (theme: EScratchTicketTheme) => void;
  isFinished: Record<EScratchTicketTheme, boolean>;
  bonus: Record<EScratchTicketTheme, LootboxBonus | null>;
  setIsFinished: (theme: EScratchTicketTheme, value: boolean) => void;
  setBonus: (theme: EScratchTicketTheme, value: LootboxBonus | null) => void;
}

export interface FortuneWheelState {
  fortuneWheelMenuData: FortuneWheelMenuDataApi | null;
  pendingFortuneWheelMenuData: boolean;
  fetchFortuneWheelMenuData: (locale: string) => Promise<void>;
}
export interface DiceRollState {
  diceRollMenuData: DiceRollMenuDataApi | null;
  pendingDiceRollMenuData: boolean;
  fetchDiceRollMenuData: (locale: string) => Promise<void>;
}

export interface LoyaltySseState {
  // State
  loyaltySseState: ISSEInitEvent | null;
  loyaltySseIsLoading: boolean;

  // Actions
  subscribeLoyaltySse: () => void;
  unsubscribeLoyaltySse: () => void;
}

/**
 * Server-side prefetched games state
 * Contains game data fetched during SSR for specific categories
 */
export interface ServerPrefetchedGamesState {
  /**
   * Map of games list keys (see getGamesListKey) to their game responses
   * Populated during server-side rendering, empty object by default
   */
  gamesByCategory?: Record<string, GamesFilter.Response | null>;
}

export type CommonStoreType = TechInfoState &
  PlayerState &
  AuthState &
  AddonsState &
  RealTimeServiceState &
  GamesState &
  TournamentsState &
  HeadlinePromoState &
  BonanzaPromoState &
  ScratchGameState &
  FortuneWheelState &
  DiceRollState &
  LoyaltySseState &
  ServerPrefetchedGamesState;
